import { useEffect, useState } from "react"
import { api } from "../../lib/api"
import type { Compra } from "../../types"
import { dataCurta, reais } from "../../lib/format"
import { Botao, Card } from "../ui"

// O QR fica na própria página — sair do site para pagar é onde o checkout
// perde gente. Enquanto o candidato paga, consultamos o status: quem confirma
// de verdade é o webhook (ou a reconciliação, se ele se perder).
export default function Pix({
  compra,
  aoLiberar,
  aoVoltar,
}: {
  compra: Compra
  aoLiberar: () => void
  aoVoltar: () => void
}) {
  const [status, setStatus] = useState(compra.status)
  const [copiado, setCopiado] = useState(false)

  useEffect(() => {
    if (status !== "pendente") return undefined

    const intervalo = setInterval(async () => {
      const atual = await api.get<Compra>(`/compras/${compra.id}`)
      setStatus(atual.status)
      if (atual.status === "paga") {
        clearInterval(intervalo)
        setTimeout(aoLiberar, 1500)
      }
    }, 4000)

    return () => clearInterval(intervalo)
  }, [compra.id, status, aoLiberar])

  if (status === "paga") {
    return (
      <Card className="border-emerald-300 text-center">
        <p className="text-lg font-bold text-emerald-700">Pagamento confirmado</p>
        <p className="mt-1 text-sm text-slate-600">Seu acesso já está liberado. Bons estudos.</p>
      </Card>
    )
  }

  // Cancelada ou estornada: mostrar o QR de novo seria convidar a pagar uma
  // cobrança que não existe mais.
  if (status !== "pendente") {
    return (
      <Card className="space-y-3 text-center">
        <p className="text-lg font-bold text-navy-900">
          {status === "estornada" ? "Pagamento estornado" : "Este Pix não vale mais"}
        </p>
        <p className="text-sm text-slate-600">
          {status === "estornada"
            ? "O valor volta pela mesma chave Pix. Se não era isso que você queria, fale com o suporte."
            : "Nada foi cobrado. Volte e gere um novo quando quiser pagar."}
        </p>
        <Botao variante="fantasma" onClick={aoVoltar}>
          Voltar
        </Botao>
      </Card>
    )
  }

  return (
    <Card className="space-y-4 text-center">
      <div>
        <h1 className="text-lg font-bold text-navy-900">{compra.produto}</h1>
        <p className="text-2xl font-extrabold text-navy-900">{reais(compra.valor_centavos)}</p>
      </div>

      {compra.pix_qrcode_base64 && (
        <img
          src={`data:image/png;base64,${compra.pix_qrcode_base64}`}
          alt="QR Code do Pix"
          className="mx-auto h-56 w-56 rounded-xl border border-navy-100 bg-white p-2"
        />
      )}

      {/* A ordem é a do gesto: copiar, ir ao banco, voltar. "Abra o app do seu
          banco" era a primeira instrução — e trocar de app é exatamente o que
          mata esta tela no navegador embutido do Gmail ou do app do Google. A
          pessoa voltava sem QR, sem sessão e sem saber que o Pix ainda valia. */}
      <div className="space-y-2">
        <p className="text-sm text-slate-600">
          Copie o código (ou escaneie o QR), pague no app do seu banco e volte quando quiser.
        </p>
        <Botao
          onClick={async () => {
            await navigator.clipboard.writeText(compra.pix_copia_e_cola ?? "")
            setCopiado(true)
            setTimeout(() => setCopiado(false), 2000)
          }}
        >
          {copiado ? "Copiado" : "Copiar código"}
        </Botao>
        <p className="break-all rounded-xl bg-navy-50 p-3 text-left font-mono text-xs text-navy-900">
          {compra.pix_copia_e_cola}
        </p>
      </div>

      <p className="text-sm text-slate-500">
        O acesso libera sozinho assim que o pagamento cair, mesmo que esta tela feche.
        {compra.pix_vence_em && <> Este Pix vale até {dataCurta(compra.pix_vence_em)}.</>}
      </p>

      <button type="button" onClick={aoVoltar} className="text-sm text-slate-600 underline">
        Voltar
      </button>
    </Card>
  )
}
